import { isPlatformServer } from '@angular/common';
import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  HostListener,
  Inject,
  OnInit,
  PLATFORM_ID,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { FxRateService } from '@univacity/angular/datas';
import {
  AngularUiModule,
  ColorSchemeService,
  ScreenSizeService,
} from '@univacity/angular/ui';
import { PAYMENT_CURRENCY } from '@univacity/shared/types';
import {
  NzNotificationComponent,
  NzNotificationService,
} from 'ng-zorro-antd/notification';
import { filter, interval } from 'rxjs';
import { environment } from '../environments/environment.demo';

@Component({
  imports: [RouterModule, AngularUiModule],
  selector: 'un-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.less',
})
export class AppComponent implements AfterViewInit, OnInit, AfterViewChecked {
  @ViewChild('updateNotificatiion', { static: true })
  updateNotificatiion!: TemplateRef<{
    $implicit: NzNotificationComponent;
    data: void;
  }>;

  constructor(
    private screenSize: ScreenSizeService,
    private colorScheme: ColorSchemeService,
    private fxRate: FxRateService,
    @Inject(PLATFORM_ID) private platformId: object,
    private updates: SwUpdate,
    private notification: NzNotificationService
  ) {}

  ngOnInit(): void {
    this.setCurrencyRate();
    this.colorScheme.init();
  }

  ngAfterViewInit(): void {
    this.setScreenSize();
  }

  ngAfterViewChecked(): void {
    this.checkForUpdate();
    this.watchForUpdate();
  }

  private watchForUpdate() {
    if (isPlatformServer(this.platformId)) return;
    this.updates.versionUpdates
      .pipe(
        filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY')
      )
      .subscribe(() => {
        this.notiifyUpdate();
      });
  }

  private checkForUpdate() {
    if (isPlatformServer(this.platformId)) return;

    const every3Hours$ = interval(3 * 60 * 60 * 1000);
    every3Hours$.subscribe(async () => {
      try {
        const updateFound = await this.updates.checkForUpdate();
        if (updateFound) this.notiifyUpdate();
      } catch (err) {
        console.error('Failed to check for updates:', err);
      }
    });
  }

  notiifyUpdate() {
    this.notification.template(this.updateNotificatiion, {
      nzDuration: 30000,
      nzPauseOnHover: true,
      nzKey: 'appUpdate',
      nzAnimate: true,
      nzPlacement: 'bottomRight',
      nzData: {},
    });
  }

  reload(notification: NzNotificationComponent) {
    document.location.reload();
    notification.close();
  }

  setCurrencyRate() {
    this.fxRate.update(PAYMENT_CURRENCY.USD);
  }

  @HostListener('window:resize', ['$event'])
  private setScreenSize() {
    if (isPlatformServer(this.platformId)) return;
    const width = window.innerWidth;
    if (width <= environment.screen.sm) this.screenSize.setSize('small');
    else if (width <= environment.screen.md) this.screenSize.setSize('medium');
    else if (width < environment.screen.lg) this.screenSize.setSize('large');
  }
}
